import dayjs from "dayjs";
import type {
  LogItemType,
  LogAttributesType,
  ImpersonationContextEntry,
} from "@/types/common/logs";
import { Avatar, AvatarFallback, Icon } from "@elaniintc/goes-design-system";
import {
  isImpersonationEvent,
  getChanges,
  getChangeTitle,
  actionTranslator,
  logTypeTranslator,
  logMessageValidation,
} from "./logs.helpers";

export const Description = ({
  log,
  entity,
}: {
  log: LogItemType;
  entity?: string;
}) => {
  if (isImpersonationEvent(log.event)) {
    return (
      <h3 className="mb-2 font-semibold">Cambio de contexto de usuario</h3>
    );
  }

  const changes = getChanges(
    log.properties?.old as LogAttributesType,
    log.properties?.attributes as LogAttributesType,
    log.subject_type,
  );

  const statusChange = changes.find((c) => c.name === "status");
  const isArchived = statusChange?.current === "INACTIVE";

  // Verificar fechas programadas independientemente del cambio de estado
  const scheduledFrom = log.properties?.attributes?.scheduled_inactive_from;
  const scheduledTo = log.properties?.attributes?.scheduled_inactive_to;

  // Si hay fechas programadas, mostrarlas primero
  if (scheduledFrom && scheduledTo) {
    return (
      <h3 className="mb-2">
        Se programó la inactivación del proceso desde{" "}
        {dayjs(scheduledFrom).format("DD/MM/YYYY [a las] HH:mm")} hasta{" "}
        {dayjs(scheduledTo).format("DD/MM/YYYY [a las] HH:mm")}
      </h3>
    );
  }

  if (isArchived) {
    const from = log.properties?.attributes?.inactive_from;
    const to = log.properties?.attributes?.inactive_to;

    if (from && to) {
      return (
        <h3 className="mb-2">
          Se archivó el proceso desde {dayjs(from).format("DD/MM/YYYY")} hasta{" "}
          {dayjs(to).format("DD/MM/YYYY")}
        </h3>
      );
    }

    return <h3 className="mb-2">Se archivó el proceso</h3>;
  }

  // Expedient associate added/updated messages
  const expedientTitle = getChangeTitle(
    log.action,
    log.subject_type,
    "",
    entity,
  );
  if (expedientTitle) {
    return <h3 className="mb-2">{expedientTitle}</h3>;
  }

  if (changes.length) {
    const titles = [
      ...new Set(
        changes.map((c) =>
          getChangeTitle(c.name, log.subject_type, "", entity),
        ),
      ),
    ];
    return <h3 className="mb-2">{titles.join(", ")}</h3>;
  }

  if (log.subject_type || log.action) {
    const translatedAction = actionTranslator(log.action);
    if (translatedAction) {
      return (
        <h3 className="mb-2">
          {log.subject_type && `${logTypeTranslator(log.subject_type)} `}
          {translatedAction}
          {": "} {logMessageValidation(log)}
        </h3>
      );
    }
    // Fallback if action is not translatable but description exists
    if (log.description) {
      return <h3 className="mb-2">{log.description}</h3>;
    }
  }

  return <h3 className="mb-2">{log.description}</h3>;
};

const AvatarContent = ({
  name,
  category,
  className = "",
}: {
  name: string;
  category: string;
  className?: string;
}) => {
  if (category === "LEGAL_PERSON") {
    return <Icon type="fas" name="buildings" className={className} />;
  }
  return <>{name.charAt(0).toUpperCase()}</>;
};

export const ImpersonationBanner = ({ log }: { log: LogItemType }) => {
  if (!isImpersonationEvent(log.event)) return null;

  const subject = log.subject;
  const contextNew = log.properties?.context
    ?.new as ImpersonationContextEntry | null;
  const contextPrevious = log.properties?.context
    ?.previous as ImpersonationContextEntry | null;

  const getSubjectName = (): string => {
    if (subject?.name) return subject.name;
    return `${subject?.first_name ?? ""} ${subject?.last_name ?? ""}`.trim();
  };

  let fromEntry: Pick<ImpersonationContextEntry, "name" | "category"> | null =
    null;
  let toEntry: Pick<ImpersonationContextEntry, "name" | "category"> | null =
    null;

  if (log.event === "impersonation_activated") {
    fromEntry = {
      name: getSubjectName(),
      category:
        (subject?.category as ImpersonationContextEntry["category"]) ?? "",
    };
    toEntry = contextNew
      ? { name: contextNew.name, category: contextNew.category }
      : null;
  } else if (log.event === "impersonation_deactivated") {
    fromEntry = contextPrevious
      ? { name: contextPrevious.name, category: contextPrevious.category }
      : null;
    toEntry = {
      name: getSubjectName(),
      category:
        (subject?.category as ImpersonationContextEntry["category"]) ?? "",
    };
  } else if (log.event === "impersonation_changed") {
    fromEntry = contextPrevious
      ? { name: contextPrevious.name, category: contextPrevious.category }
      : null;
    toEntry = contextNew
      ? { name: contextNew.name, category: contextNew.category }
      : null;
  }

  if (!fromEntry || !toEntry) return null;

  return (
    <div className="bg-neutral-object-accent-silent border border-neutral-quiet rounded-lg p-2 flex gap-2 items-center mb-2">
      <div className="flex items-center gap-0.5">
        <Avatar className="shrink-0" size="xs">
          <AvatarFallback color="neutral">
            <AvatarContent
              name={fromEntry.name}
              category={fromEntry.category}
            />
          </AvatarFallback>
        </Avatar>
        <span className="text-base text-on-disabled line-through">
          {fromEntry.name}
        </span>
      </div>

      <Icon
        type="far"
        name="arrow-right"
        size="sm"
        className="text-body-quiet"
      />

      <div className="flex items-center gap-1">
        <Avatar className="shrink-0" size="xs">
          <AvatarFallback color="action">
            <AvatarContent
              name={toEntry.name}
              category={toEntry.category}
              className="text-action-quiet!"
            />
          </AvatarFallback>
        </Avatar>
        <span className="text-base text-action">{toEntry.name}</span>
      </div>
    </div>
  );
};
